import React, { useState, useEffect, useMemo, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Modal,
  TextField,
  CircularProgress,
  List,
  ListItemButton,
  ListItem,
  ListItemText,
  FormControl,
  LinearProgress,
  InputLabel,
  Select,
  Checkbox,
  FormGroup,
  FormControlLabel
} from '@mui/material'
import {
  Search,
  DriveFileRenameOutline,
  FileUpload,
  CreateNewFolder,
  DeleteOutline,
  Sort,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ChevronLeft,
  Close,
  UploadFile
} from '@mui/icons-material'
import MenuIcon from '@mui/icons-material/Menu'
import { SelectChangeEvent } from '@mui/material/Select'
import useResizeObserver from "use-resize-observer"
import { v4 as uuidv4 } from 'uuid'
import { LuCodeXml } from "react-icons/lu"
import { VscJson } from "react-icons/vsc"

import { get, post, put, remove, customPost, fetchPost } from '../../utils/http'
import RenderTree from './RenderTree'
import { TreeNode, FullNodeProp, ImageData } from '../../utils/types'
import { modalStyle } from '../../styles/js/common'
import UploadSlide from './helper/UploadSlide'
import LogInfoTable from './helper/LogInfoTable'
import SlideInfo from './helper/SlideInfo'
import PredictionForm from './helper/predictionForm'
import ShareSlide from './helper/ShareSlide'
import { outputS3, isPrivate } from '../../utils/config'
import PatientList from './helper/PatientList'
import { Dotpulse } from '../common/Loaders'


interface FilePickerAcceptType {
  description?: string;
  accept: Record<string, string[]>;
}

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: FilePickerAcceptType[];
}

interface FileSystemWritableFileStream extends WritableStream {
  write(data: any): Promise<void>;
  seek(position: number): Promise<void>;
  truncate(size: number): Promise<void>;
}

declare global {
  interface FileSystemFileHandle {
    createWritable(options?: { keepExistingData?: boolean }): Promise<FileSystemWritableFileStream>;
  }
  interface Window {
    showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
  }
}

interface DirectoriesProps {
  handleShowTree: Function,
  selectSlide: Function,
  toggleCompareViewer: Function,
  compareImage: boolean,
  resetMap: Function
}

const Directories: React.FC<DirectoriesProps> = ({
  handleShowTree,
  selectSlide,
  toggleCompareViewer,
  compareImage,
  resetMap
}) => {
  const abortControllerRef = useRef<AbortController | null>(null)

  const [mainTree, setMainTreeData] = useState<TreeNode>({})
  const [sharedTree, setSharedTreeData] = useState<TreeNode>({})
  const [mountedTree, setMountedTreeData] = useState<TreeNode>({})
  const [s3Tree, setS3TreeData] = useState<TreeNode>({})
  const [binTree, setBinTreeData] = useState<TreeNode>({'Bin/': {id: '4', created: new Date().toDateString()}}) // eslint-disable-line

  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [selectedNode, setSelectedNode] = useState<FullNodeProp | null>(null)
  const [nodeProp, setNodeProp] = useState<FullNodeProp>({ id: '', name: '', created: '' })
  const [searchKey, setSearchKey] = useState<string>('')
  const [s3SearchKey, setS3SearchKey] = useState<string>('')
  const [searchFinds, setSearchFinds] = useState<string[]>([])
  const [focusNodeId, setFocusNodeId] = useState<string>('')  // eslint-disable-line
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [slideForm, setSlideForm] = useState<{[key: string]: any}>({prediction_type: 'bcr', request_type: 'start'})
  const [downloadListType, setDownloadListType] = useState<string>('XML')
  const [sortType, setSortType] = useState<string | null>(null)
  const [copiedNode, setCopiedNode] = useState<FullNodeProp | null>(null)

  const [sortMenuEl, setSortMenuEl] = useState<HTMLElement | null>(null)
  const sortMenuOpen = Boolean(sortMenuEl)
  const [nodeMenuEl, setNodeMenuEl] = useState<HTMLElement | null>(null)
  const nodeMenuOpen = Boolean(nodeMenuEl)

  const [isFileTransfer, setIsFileTransfer] = useState<boolean>(false)
  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const [s3SearchOpen, setS3SearchOpen] = useState<boolean>(false) 
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
  const [uploadOpen, setUploadOpen] = useState<boolean>(false)
  const [showCheckbox, setShowCheckbox] = useState<boolean>(false)
  const [shareOpen, setShareOpen] = useState<boolean>(false)
  const [syncS3Open, setSyncS3Open] = useState<boolean>(false)
  const [apiWaiting, setApiWaiting] = useState<boolean>(false)
  const [logsOpen, setLogsOpen] = useState<boolean>(false)
  const [slideInfoOpen, setSlideInfoOpen] = useState<boolean>(false)
  const [openPredictionForm, setOpenPredictionForm] = useState<boolean>(false)
  const [annoListOpen, setAnnoListOpen] = useState<boolean>(false)
  const [openUploadToSlide, setOpenUploadToSlide] = useState<boolean>(false)
  const [openPredictFolder, setOpenPredictFolder] = useState<boolean>(false)
  const [openDownlFolderData, setOpenDownlFolderData] = useState<boolean>(false)
  const [uploadS3Slides, setUploadS3Slides] = useState<boolean>(false)
  const [openCopyPasteConfirm, setOpenCopyPasteConfirm] = useState<boolean>(false)
  const [showPatientModal, setShowPatientModal] = useState<boolean>(false)
  const [edited_only, setEdited_only] = useState<boolean>(false)

  // Rename node
  const [renameOpen, setRenameOpen] = useState<boolean>(false)
  const [nodeNameError, setNodeNameError] = useState<string>('')
  const RESERVED_NAMES = new Set(['mounts', 'shared', 'my folder', 'bin'])

  const [downloadedBytes, setDownloadedBytes] = useState<number>(0)

  const isDev = /dev/i.test(localStorage.getItem('role') || 'user')  // eslint-disable-line
  const userId: number = Number(localStorage.getItem('user_id') || '1')
  const attachedS3 = 'master-dataset'

  const { ref, width = 0, height = 0 } = useResizeObserver<HTMLDivElement>()

  // Fetch tree data

  // My folder
  const fetchMainTreeData = async () => {
    try {
      const res = await get(`user-tree/user/${userId}`)
      if ('My Folder/' in res) setMainTreeData(res)
      else throw Error('nf')
    } catch (err) {
      setMainTreeData({'My Folder/': {id: '0', created: new Date().toDateString()}})
    }
  }
  // Mounts folder
  const fetchMountedTreeData = async () => {
    try {
      if (!isPrivate) throw Error('nf')
      const res = await get(`user-mounted-tree/user/${userId}`)
      if ('Mounts/' in res) setMountedTreeData(res)
      else throw Error('nf')
    } catch (err) {
      setMountedTreeData({'Mounts/': {id: '1', created: new Date().toDateString()}})
    }
  }
  // Shared folder
  const fetchSharedTreeData = async () => {
    try {
      const res = await get(`shared-tree/user/${userId}`)
      if ('Shared/' in res) setSharedTreeData(res)
      else throw Error('nf')
    } catch (err) {
      setSharedTreeData({'Shared/': {id: '2', created: new Date().toDateString(), shared: true}})
    }
  }
  // Initial S3 folder data
  const fetchS3TreeData = async () => {
    try {
      if (!isPrivate) throw Error('nf')
      const res = await post('S3/treedata', { s3_name: attachedS3, directory: '' })
      if (`${attachedS3}/` in res) setS3TreeData(prevState => ({...prevState, ...res}))
      else throw Error('nf')
    } catch (err) {
      setS3TreeData({'master-dataset/': {id: '3', created: new Date().toDateString()}})
    }
  }
  
  useEffect(() => {
    fetchMainTreeData()
    fetchMountedTreeData()
    fetchSharedTreeData()
    fetchS3TreeData()
  }, [])  // eslint-disable-line

  const handleCancelDownload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  // Warn user about downloading when navigating away from the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => { if (isFileTransfer) e.preventDefault() }
    if (isFileTransfer) window.addEventListener('beforeunload', handleBeforeUnload)
    else window.removeEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isFileTransfer])

  const updateMainTree = (data: TreeNode) => setMainTreeData(prevState => ({...prevState, ...data}))
  const updateMountedTree = (data: TreeNode) => setMountedTreeData(prevState => ({...prevState, ...data}))

  // View selected slide in the viewer
  const viewSlide = async (nodeData: FullNodeProp) => {
    try {
      if ('is_mounted' in nodeData && nodeData.is_mounted) {
        // Check DB for existing row if not create a new slide mount without adding it to Mounts/
        // Show loading animation
        const payload = {
          user_id: userId,
          s3_dir: nodeData.s3_dir,
          s3_name: nodeData.s3_name,
          name: nodeData.s3_dir + '/' + nodeData.name.split('/').pop()
        }
        const res: ImageData = await post('Images/check-mount-slide', payload)
        const newNode = {
          id: uuidv4(),
          file_id: res.image_unique_id,
          name: nodeData.name,
          s3_name: res.input_s3!,
          s3_dir: nodeData.s3_dir,
          created: new Date().toISOString()
        }
        setSelectedNode(newNode)
        setS3TreeData(prevState => ({...prevState, [nodeData.name]: newNode}))
        selectSlide(newNode, compareImage)
      } else selectSlide(nodeData, compareImage)
    } catch (err) {
      console.log(err)
    }
  }

  // Main toolbar events

  // Search node
  const handleSearch = () => setSearchOpen(true)
  const closeSearchModal = () => {
    setSearchKey('')
    setSearchOpen(false)
  }
  const handleSearchKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => setSearchKey(e.target.value)

  const handleS3Search = () => {
    closeNodeMenu()
    setS3SearchOpen(true)
  }
  const closeS3SearchModal = () => {
    setS3SearchKey('')
    setSearchFinds([])
    setS3SearchOpen(false)
  }
  const handleS3SearchKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    document.querySelector('#search-key-error')?.classList.add('d-none')
    setS3SearchKey(e.target.value)
  }
  const searchS3 = async (key: string) => {
    try {
      if (!isPrivate) throw Error('Not internal ver')
      const t = selectedNode!.name.split('/').filter(Boolean)
      const payload = {
        key: key,
        s3_name: selectedNode!.s3_name,
        directory: selectedNode!.name.endsWith('/') ? t.slice(1).join('/') : t.slice(1, -1).join('/'),
        continuation_token: null
      }
      const res = await post('S3/search-name', payload)
      return res.search_results
    } catch (err) {
      return []
    }
  }
  const searchNodeName = async () => {
    setApiWaiting(true)
    try {
      const key = s3SearchKey.trim().toLowerCase().replace(/^\/+|\/+$/g, '')
      if (!key.length || !selectedNode) throw Error('no key or selected node')
      const finds = await searchS3(key)
      if (!finds.length) throw Error('key not found')
      setSearchFinds(finds)
    } catch (err) {
      console.log(err)
      setSearchFinds([])
      document.querySelector('#search-key-error')?.classList.remove('d-none')
    } finally {
      setApiWaiting(false)
    }
  }
  const selectSearchFind = (index: number) => {
    // 'is_mounted' in imageData condition is for the mounted slides from S3 buckets (now only for master-dataset)
    const tempNode = {
      id: uuidv4(),
      file_id: uuidv4(),
      name: `${attachedS3}/${searchFinds[index]}`,
      s3_name: selectedNode!.s3_name,
      s3_dir: searchFinds[index].split('/').filter(Boolean).slice(0, -1).join('/'),
      is_mounted: true,
      created: new Date().toISOString()
    }
    viewSlide(tempNode)
    closeS3SearchModal()
  }

  // Upload all files in the S3 folder to database
  const handleS3BatchUpload = () => setUploadS3Slides(true)
  const closeUploadS3Slides = () => setUploadS3Slides(false)
  const continueUplaodS3Slides = async () => {
    setApiWaiting(true)
    try {
      const typesC = ['prostate', 'lung', 'bladder', 'breast']
      const typesS = ['biopsy', 'rp']
      const currentDir = selectedNode!.name.replace(`${attachedS3}/`, '')
      
      const res = await post('Images/s3-batch', {
        user_id: userId,
        cancer_type: typesC.find(i => currentDir.includes(i.toLowerCase())),
        slide_type: typesS.find(i => currentDir.includes(i.toLowerCase())),
        input_s3: attachedS3,
        input_s3_dir: currentDir,
        output_s3: 'master-research'
      })
      console.log(res)
    } catch (err) {
      console.log(err)
    } finally {
      setApiWaiting(false)
      closeUploadS3Slides()
    }
  }

  // Rename node
  const handleRename = () => {
    if (selectedNode) setNodeProp(selectedNode) 
    setRenameOpen(true)
  }

  const closeNodeNameModal = () => {
    setRenameOpen(false)
    setNodeNameError('')
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.indexOf('/') !== -1) setNodeNameError('"/" not suppoted.')
    else setNodeNameError('')

    setNodeProp(prevState => ({...prevState, [e.target.name]: e.target.value}))
  }

  const renameNode = async () => {
    setApiWaiting(true)
    try {
      const oldName = selectedNode!.name
      const newPropName = nodeProp.name
      const isDir = oldName.endsWith('/')
      const trimmedOld = isDir ? oldName.slice(0, -1) : oldName
      const lastSlashIdx = trimmedOld.lastIndexOf('/')
      const basePath = lastSlashIdx !== -1 ? trimmedOld.slice(0, lastSlashIdx + 1) : ''
      const nodeName = `${basePath}${newPropName}${isDir ? '/' : ''}`
      const lowerCaseName = newPropName.toLowerCase()
      const isReserved = RESERVED_NAMES.has(lowerCaseName) || lowerCaseName === attachedS3

      if (isReserved) {
        throw new Error(`"${newPropName}" is a reserved system or folder name.`)
      }
      if (newPropName.startsWith('<') && newPropName.indexOf('>_') > 0) {
        throw new Error(`"${newPropName}" contains restricted system patterns.`)
      }

      if (oldName.startsWith('My Folder/')) {
        if (nodeName in mainTree) {
          throw new Error(`"${newPropName}" already exists.`)
        }

        const res = await put(`user-tree/user/${userId}`, { old_name: oldName, new_name: nodeName })
        setMainTreeData(prevState => {
          const newState = { ...prevState }
          for (const oldKey of res.old_names) delete newState[oldKey]
          return { ...newState, ...res.new }
        })
        setSelectedNode(prevState => ({...prevState!, name: Object.keys(res.new)[0]}))
        
      } else if (oldName.startsWith('Mounts/')) {
        if (nodeName in mountedTree) {
          throw new Error(`"${newPropName}" already exists in Mounts.`)
        }

        const res = await put(`user-mounted-tree/user/${userId}`, { old_name: oldName, new_name: nodeName })
        setMountedTreeData(prevState => {
          const newState = { ...prevState }
          for (const oldKey of res.old_names) delete newState[oldKey]
          return { ...newState, ...res.new }
        })
        setSelectedNode(prevState => ({...prevState!, name: Object.keys(res.new)[0]}))
      }

      closeNodeNameModal()
    } catch (e) {
      console.error(e)
      const errorMessage = e instanceof Error ? e.message : String(e)
      setNodeNameError(errorMessage)
    } finally {
      setApiWaiting(false)
    }
  }

  // Upload files to tree
  const handleUploadNode = () => setUploadOpen(true)
  const handleUploadClose = () => setUploadOpen(false)
  const saveFile = async (newNodes: TreeNode) => {
    setApiWaiting(true)
    try {
      setMainTreeData(prevState => ({...prevState, ...newNodes}))
    } catch (err) {
      console.log(err)
    } finally {
      setApiWaiting(false)
    }
  }
  const saveMounts = async (newNodes: TreeNode) => {
    setApiWaiting(true)
    try {
      if (!Object.keys(newNodes).length) throw Error('Nothing to update')
      setMountedTreeData(prevState => ({...prevState, ...newNodes}))
    } catch (err) {
      console.log(err)
    } finally {
      setApiWaiting(false)
    }
  }

  // New folder
  const createNode = async () => {
    setApiWaiting(true)
    try {
      const selectedName = selectedNode!.name
      const newPropName = nodeProp.name
      const basePath = selectedName.endsWith('/') ? selectedName : selectedName.slice(0, selectedName.lastIndexOf('/') + 1)
      const nodeName = `${basePath}${newPropName}/`
      const lowerCaseName = newPropName.toLowerCase()
      const isReserved = RESERVED_NAMES.has(lowerCaseName) || lowerCaseName === attachedS3

      if (newPropName.startsWith('<') && newPropName.indexOf('>_') > 0) {
        throw new Error(`"${newPropName}" contains restricted system patterns.`)
      }
      if (isReserved) {
        throw new Error(`"${newPropName}" is a reserved system or folder name.`)
      }

      if (selectedNode!.name.startsWith('My Folder/')) {
        if (nodeName in mainTree) {
          throw new Error(`"${newPropName}" already exists.`)
        }

        const res = await post(`user-tree/user/${userId}`, { name: nodeName })
        setMainTreeData(prevState => ({ ...prevState, ...res }))
      } else if (selectedNode!.name.startsWith('Mounts/')) {
        if (nodeName in mountedTree) {
          throw new Error(`"${newPropName}" already exists in Mounts.`)
        }

        const res = await post(`user-mounted-tree/user/${userId}`, { name: nodeName })
        setMountedTreeData(prevState => ({...prevState, ...res}))
      }
      
      closeNodeNameModal()
    } catch (e) {
      console.error(e)
      const errorMessage = e instanceof Error ? e.message : String(e)
      setNodeNameError(errorMessage)
    } finally {
      setApiWaiting(false)
    }
  }

  const handleNewNode = () => {
    setNodeProp({id: '', name: '', created: ''})
    setRenameOpen(true)
  }

  // Delete node
  const handleDelete = () => setDeleteOpen(true)
  const closeDeleteModal = () => setDeleteOpen(false)
  const deleteNode = async () => {
    setApiWaiting(true)
    try {
      if (selectedNode!.name.startsWith('My Folder/')) {
        const res = await remove(`user-tree/user/${userId}`, {name : selectedNode!.name})
        setMainTreeData(prevState => Object.fromEntries(Object.entries(prevState).filter(([key]) => !res.includes(key))))
      } else if (selectedNode!.name.startsWith('Mounts/')) {
        const res = await remove(`user-mounted-tree/user/${userId}`, {name : selectedNode!.name})
        setMountedTreeData(prevState => Object.fromEntries(Object.entries(prevState).filter(([key]) => !res.includes(key))))
      }
      setSelectedNode(null)
    } catch (err) {
      console.log(err)
    } finally {
      setApiWaiting(false)
      closeDeleteModal()
    }
  }

  // Sort ops
  const handleSortMenu = (e: React.MouseEvent<HTMLElement>) => setSortMenuEl(e.currentTarget)
  const closeSortMenu = () => setSortMenuEl(null)
  const handleSortTree = (val: string) => setSortType(val)
  const sortTree = () => {
    if (!sortType || !selectedNode) return

    if (selectedNode.name.startsWith('My Folder/')) {
      if (sortType.startsWith('name')) {
        setMainTreeData(prevItems => {
          const temp = {...prevItems}
          const keys = Object.keys(temp).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          if (sortType.endsWith('desc')) keys.reverse()
          return keys.reduce((acc: any, key: string) => {
            acc[key] = temp[key]
            return acc
          }, {})
        })
      } else {  // Date sort
        setMainTreeData(prevItems => {
          const temp = {...prevItems}
          return Object.entries(temp).sort(([, a], [, b]) =>
            sortType.endsWith('asc')
              ? new Date(a.created).getTime() - new Date(b.created).getTime()
              : new Date(b.created).getTime() - new Date(a.created).getTime()
          ).reduce((acc: any, [key, value]) => {
            acc[key] = value
            return acc
          }, {})
        })
      }
    } else if (selectedNode.name.startsWith('Mounts/')) {
      if (sortType.startsWith('name')) {
        setMountedTreeData(prevItems => {
          const temp = {...prevItems}
          const keys = Object.keys(temp).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          if (sortType.endsWith('desc')) keys.reverse()
          return keys.reduce((acc: any, key: string) => {
            acc[key] = temp[key]
            return acc
          }, {})
        })
      } else {  // Date sort
        setMountedTreeData(prevItems => {
          const temp = {...prevItems}
          return Object.entries(temp).sort(([, a], [, b]) =>
            sortType.endsWith('asc')
              ? new Date(a.created).getTime() - new Date(b.created).getTime()
              : new Date(b.created).getTime() - new Date(a.created).getTime()
          ).reduce((acc: any, [key, value]) => {
            acc[key] = value
            return acc
          }, {})
        })
      }
    } else if (selectedNode.name.startsWith('Shared/')) {
      if (sortType.startsWith('name')) {
        setSharedTreeData(prevItems => {
          const temp = {...prevItems}
          const keys = Object.keys(temp).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          if (sortType.endsWith('desc')) keys.reverse()
          return keys.reduce((acc: any, key: string) => {
            acc[key] = temp[key]
            return acc
          }, {})
        })
      } else {  // Date sort
        setSharedTreeData(prevItems => {
          const temp = {...prevItems}
          return Object.entries(temp).sort(([, a], [, b]) =>
            sortType.endsWith('asc')
              ? new Date(a.created).getTime() - new Date(b.created).getTime()
              : new Date(b.created).getTime() - new Date(a.created).getTime()
          ).reduce((acc: any, [key, value]) => {
            acc[key] = value
            return acc
          }, {})
        })
      }
    } else if (selectedNode.name.startsWith('master-dataset/')) {
      if (sortType.startsWith('name')) {
        setS3TreeData(prevItems => {
          const temp = {...prevItems}
          const keys = Object.keys(temp).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
          if (sortType.endsWith('desc')) keys.reverse()
          return keys.reduce((acc: any, key: string) => {
            acc[key] = temp[key]
            return acc
          }, {})
        })
      } else {  // Date sort
        setS3TreeData(prevItems => {
          const temp = {...prevItems}
          return Object.entries(temp).sort(([, a], [, b]) =>
            sortType.endsWith('asc')
              ? new Date(a.created).getTime() - new Date(b.created).getTime()
              : new Date(b.created).getTime() - new Date(a.created).getTime()
          ).reduce((acc: any, [key, value]) => {
            acc[key] = value
            return acc
          }, {})
        })
      }
    }
  }
  useEffect(() => {
    sortTree()
  }, [sortType])  // eslint-disable-line

  // Node menu
  const handleNodeMenu = (e: React.MouseEvent<HTMLElement>) => setNodeMenuEl(e.currentTarget)
  const closeNodeMenu = () => setNodeMenuEl(null)

  // Show checkbox for select/unselect node items To-Do
  const handleSelectNodeItems = () => { // eslint-disable-line
    if (showCheckbox) {
      setSelectedItems([])
      setSelectedNode(null)
      setNodeProp({id: '', name: '', created: ''})
    }
    setShowCheckbox(prevState => !prevState)
  }

  const copyNode = () => setCopiedNode(selectedNode)

  const confirmCopyPaste = () => {
    closeNodeMenu()
    setOpenCopyPasteConfirm(true)
  }

  const cancelCopyPaste = () => {
    setOpenCopyPasteConfirm(false)
    setCopiedNode(null)
  }

  const handleNodePaste = async () => {
    setApiWaiting(true)
    try {
      if (!copiedNode) return

      const copy_node = {...copiedNode}
      setCopiedNode(null)
      
      if (!copy_node.name.endsWith('/') && 'is_mounted' in copy_node && copy_node.is_mounted) {  // File in Special dir in internal app
        const payload = {
          user_id: userId,
          s3_dir: copy_node!.s3_dir,
          s3_name: copy_node!.s3_name,
          name: copy_node!.s3_dir + '/' + copy_node!.name.split('/').pop()
        }
        const imgRes: ImageData = await post('Images/check-mount-slide', payload)
        copy_node.file_id = imgRes.image_unique_id
      }
  
      const payload = {
        user_id: userId,
        copy_node: copy_node,
        dest_node: selectedNode
      }
      const res = await post('user-tree/user/copy', payload)
      setMainTreeData(prevState => ({...prevState, ...res}))
    } catch (err) {
      console.log(err)
    } finally {
      setApiWaiting(false)
      setOpenCopyPasteConfirm(false)
    }
  }

  // Toggle compare image viewer
  const compareImageViewer = () => {
    closeNodeMenu()
    toggleCompareViewer()
  }

  // Predict all files
  const predictAllFiles = () => {
    closeNodeMenu()
    setOpenPredictFolder(true)
  }
  const closePredictFolder = () => {
    setOpenPredictFolder(false)
  }
  const handleSlideForm = (e: SelectChangeEvent<string>) => {
    setSlideForm(prevForm => ({...prevForm, [e.target.name]: e.target.value}))
  }
  const predictFolder = async () => {
    setApiWaiting(true)
    try {
      const payload = { ...slideForm, node_name: selectedNode?.name, user_id: userId }
      if (selectedNode!.name.startsWith('My Folder/')) {
        await post('user-tree/predict-all', payload)
      } else if (selectedNode!.name.startsWith('Mounts/')) {
        await post('user-mounted-tree/predict-all', payload)
      } else if (selectedNode!.name.startsWith('Shared/')) {
        await post('shared-tree/predict-all', payload)
      }
    } catch (err) {
      console.log(err)
    } finally {
      closePredictFolder()
      setApiWaiting(false)
    }
  }

  // Share nodes
  const handleOpenShare = async () => {
    closeNodeMenu()
    setShareOpen(true)
  }
  const closeShareModal = () => setShareOpen(false)

  // Download All XML
  const downloadAllXMLHandler = () => {
    closeNodeMenu()
    setDownloadListType('XML')
    setOpenDownlFolderData(true)
  }
  // Download all GeoJSON
  const downloadAllGeoJSONHandler = () => {
    closeNodeMenu()
    setDownloadListType('GeoJSON')
    setOpenDownlFolderData(true)
  }

  const downloadZip = async (): Promise<void> => {
    setIsFileTransfer(true)
    setDownloadedBytes(0)
    abortControllerRef.current = new AbortController()

    try {
      const filename = selectedNode!.name.split('/').filter(Boolean).pop()!
      const payload = {
        node: selectedNode,
        user_id: userId,
        file_name: filename,
        edited_only: edited_only
      }
      const endpoint = downloadListType === 'XML' ? 'Annotations/download-xml-zip' : 'Annotations/download-json-zip'
      const res = await fetchPost(endpoint, payload, abortControllerRef.current.signal)
      if (!res.ok || !res.body) throw new Error('Failed to fetch ZIP or readable stream not supported')

      let loaded = 0
      // Explicitly type the TransformStream as handling Uint8Array data (raw bytes)
      const progressStream = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          loaded += chunk.length
          setDownloadedBytes(loaded)
          controller.enqueue(chunk)
        }
      })

      // Stream directly to disk
      if (window.showSaveFilePicker) {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: `${filename}.zip`,
          types: [{ description: 'ZIP Archive', accept: { 'application/zip': ['.zip'] } }]
        })
        const writableStream = await fileHandle.createWritable()
        await res.body.pipeThrough(progressStream).pipeTo(writableStream)
      } else {
        const reader = res.body.getReader()
        const chunks: BlobPart[] = []
        
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          if (value) {
            chunks.push(value as unknown as BlobPart)
            loaded += value.length
            setDownloadedBytes(loaded)
          }
        }
        
        const blob = new Blob(chunks, { type: 'application/zip' })
        const a = document.createElement('a')
        a.href = window.URL.createObjectURL(blob)
        a.download = `${filename}.zip`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(a.href);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        console.log('Download was intentionally canceled by the user.')
      } else if (err instanceof Error) {
        console.error('Download failed:', err.message)
      }
    } finally {
      setIsFileTransfer(false)
      closeDownlFolderData()
      abortControllerRef.current = null
    }
  }

  // Download All CSV
  const downloadAllCSVHandler = () => {
    closeNodeMenu()
    setDownloadListType('CSV')
    setOpenDownlFolderData(true)
  }
  const downloadCSV = async (): Promise<void> => {
    setIsFileTransfer(true)
    setDownloadedBytes(0)
    abortControllerRef.current = new AbortController()

    try {
      const filename = selectedNode?.name?.split('/').filter(Boolean).pop() ?? 'export'
      const payload = {
        node: selectedNode,
        user_id: userId,
        file_name: filename
      }
      
      const csv_stream = await fetchPost('Annotations/download-csv', payload, abortControllerRef.current.signal)
      if (!csv_stream.ok || !csv_stream.body) throw new Error('Failed to fetch CSV')

      let loaded = 0
      // Explicitly type the TransformStream as handling Uint8Array data (raw bytes)
      const progressStream = new TransformStream<Uint8Array, Uint8Array>({
        transform(chunk, controller) {
          loaded += chunk.length
          setDownloadedBytes(loaded)
          controller.enqueue(chunk)
        }
      })

      // Stream directly to disk
      if (window.showSaveFilePicker) {
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: `${filename}.csv`,
          types: [{ description: 'CSV Document', accept: { 'text/csv': ['.csv'] } }],
        })
        
        const writableStream = await fileHandle.createWritable()
        await csv_stream.body.pipeThrough(progressStream).pipeTo(writableStream)
      } else {
        const reader = csv_stream.body.getReader()
        const chunks: BlobPart[] = []
        
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          if (value) {
            chunks.push(value as unknown as BlobPart)
            loaded += value.length
            setDownloadedBytes(loaded)
          }
        }

        const blob = new Blob(chunks, { type: 'text/csv' })
        const a = document.createElement('a')
        a.href = window.URL.createObjectURL(blob)
        a.download = `${filename}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(a.href)
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        console.log('Download was intentionally canceled by the user.')
      } else if (err instanceof Error) {
        console.error('Download failed:', err.message)
      }
    } finally {
      setIsFileTransfer(false)
      closeDownlFolderData()
      abortControllerRef.current = null
    }
  }

  const downloadFolderData = () => {
    if (['XML', 'GeoJSON'].includes(downloadListType)) downloadZip()
    else downloadCSV()
  }
  const closeDownlFolderData = () => {
    handleCancelDownload()
    setOpenDownlFolderData(false)
    setDownloadedBytes(0)
    setEdited_only(false)
  }

  // S3 mount sync
  const handleS3MountSync = () => {
    closeNodeMenu()
    setSyncS3Open(true)
  }
  const closeS3SyncModal = () => setSyncS3Open(false)
  const syncS3Mount = async () => {
    setApiWaiting(true)
    try {
      if (!('s3_name' in selectedNode!)) throw Error('Not mounted anything yet')

      let ref_file_id: string = ''
      for (let key of Object.keys(mountedTree)) {
        if (key.startsWith(selectedNode!.name) && mountedTree[key].file_id) {
          ref_file_id = mountedTree[key].file_id!
          break
        }
      }
      const payload: {[key:string]:any} = {
        id: selectedNode!.id,
        name: selectedNode!.name,
        s3_name: selectedNode!.s3_name,
        s3_dir: selectedNode!.s3_dir,
        ref_file_id: ref_file_id,
        user_id: userId
      }
      const res = await put('Images/s3', payload)
      if (res.limit_exceeded) throw Error('Upload limit exceeded')
      else saveMounts(res)
    } catch (err) {
      console.log(err)
    } finally {
      setApiWaiting(false)
      closeS3SyncModal()
    }
  }

  // Start/Stop prediction
  const predictionStatusHandler = async () => {
    closeNodeMenu()
    setOpenPredictionForm(true)
  }
  const closePredictionForm = () => setOpenPredictionForm(false)

  const handlePatientModal = () => {
    closeNodeMenu()
    setShowPatientModal(prevState => !prevState)
  }

  // Upload XML/JSON
  const uploadXMLHandler = () => {
    closeNodeMenu()
    setOpenUploadToSlide(true)
  }
  const closeUploadToSlide = () => {
    if (isFileTransfer) return
    setOpenUploadToSlide(false)
    setUploadFile(null)
  }
  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    document.querySelector('#unknown-file-error')!.classList.add('d-none')
    if (e.target.files) {
      setUploadFile(null)
      const validSlides = Array.from(e.target.files).every((i: File) => {
        const t = i.name.split('.').pop() || 'xml'
        if (['xml', 'json', 'geojson'].includes(t.toLowerCase()) && /\//.test(i.name) === false) return true
        else return false
      })
      if (validSlides) setUploadFile(e.target.files[0])
      else document.querySelector('#unknown-file-error')!.classList.remove('d-none')
    }
  }
  const uploadFileToSlide = async () => {
    setIsFileTransfer(true)
    let reload = false
    try {
      const presignedData = await post('Annotations/upload-xml', {
        file_id: selectedNode!.file_id,
        file_name: uploadFile!.name,
        s3: outputS3
      })
      
      const s3FormData = new FormData()
      Object.keys(presignedData.fields).forEach(key => s3FormData.append(key, presignedData.fields[key]))
      s3FormData.append('file', uploadFile!)

      await customPost(presignedData.url, s3FormData, {
        headers: {"Content-Type": "multipart/form-data"}
      })

      if (/\.xml$/i.test(uploadFile!.name)) {
        await post('Annotations/reset-base', {
          file_id: selectedNode!.file_id,
          xml_name: presignedData.file_name,
          user_id: userId
        })
        reload = true
      }
    } catch (err) {
      console.log(err)
    } finally {
      closeUploadToSlide()
      setIsFileTransfer(false)
      if (reload) resetMap()
    }
  }

  const downloadFile = async (url: string, filename: string) => {
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.style.display = 'none'
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
    a.remove()
  }

  // Download image
  const downloadImageHandler = async () => {
    try {
      const res = await get(`S3/downloadimage/${selectedNode!.file_id}`)
      downloadFile(res.url, selectedNode!.name)
    } catch (err) {
      console.error(err)
    }
  }

  // Download annotation data
  const downloadAnnoHandler = () => {
    closeNodeMenu()
    setAnnoListOpen(true)
  }

  const closeAnnoDwnldOpen = () => {
    if (!isFileTransfer) setAnnoListOpen(false)
    setEdited_only(false)
  }

  const handleEditedOnlyCheck = (e: React.ChangeEvent<HTMLInputElement>) => setEdited_only(e.target.checked)

  const downloadAnnotationFile = async (fileType: string) => {
    if (!selectedNode) return
    
    setIsFileTransfer(true)
    try {
      const api = fileType === 'xml' ? 'Annotations/download-xml' : 'Annotations/download-json'
      const res = await fetchPost(api, {
        file_id: selectedNode.file_id,
        user_id: userId,
        edited_only: edited_only
      })
      if (!res.ok || !res.body) throw Error(`Failed to download ${fileType} file`)

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const filename = selectedNode.name.split('/').pop()?.replace(/\.(?=[^.]*$)/, '_')

      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `${filename}.${fileType}`
      document.body.appendChild(a)
      a.click()
      a.remove()

      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      console.log(err)
    } finally {
      setIsFileTransfer(false)
      closeAnnoDwnldOpen()
    }
  }

  // Show slide logs
  const showLogs = async () => {
    closeNodeMenu()
    setLogsOpen(true)
  }

  // Show slide info/status
  const getFileInfoHandler = () => {
    closeNodeMenu()
    setSlideInfoOpen(true)
  }

  // Reload image viewer
  const reloadViewer = () => {
    closeNodeMenu()
    resetMap()
  }

  // Restore file form bin To-Do
  // const restoreFile = async () => {} 

  // Tree events

  const selectNode = (nodePath: string) => setSelectedItems([nodePath])

  const getMoreS3Tree = async () => {
    try {
      if (!selectedNode!.name.endsWith('/')) throw Error('Not a folder')
      const childrenExist = Object.keys(s3Tree).some(key => key.startsWith(selectedNode!.name) && key.length > selectedNode!.name.length)
      if (childrenExist) throw Error('Already loaded')
      const currentDir = selectedNode!.name.replace(`${attachedS3}/`, '')
      const res = await post('S3/treedata', { s3_name: attachedS3, directory: currentDir })
      if (`${attachedS3}/` in res) setS3TreeData(prevState => ({...prevState, ...res, [selectedNode!.name]: {...selectedNode!, is_loading: false}}))
      else throw Error('nf')
    } catch (err) {
      // pass
    }
  }

  useEffect(() => {
    if (selectedNode) {
      if (selectedNode.name.startsWith(`${attachedS3}/`) && isPrivate) getMoreS3Tree()
      if (selectedNode.file_id) viewSlide(selectedNode)
    }
    // console.log(selectedNode)
  }, [selectedNode]) // eslint-disable-line

  useEffect(() => {
    if (selectedItems.length) {
      if (selectedItems.length > 1) {
        // Cancel single node selection
        setSelectedNode(null)
        setNodeProp({id: '', name: '', created: ''})
      } else {
        // Single node select
        // My folder and Mounts have proper structure but others lacks dedicated folder keys
        const nodeName = selectedItems[0]
        if (nodeName.startsWith('My Folder/') && nodeName in mainTree) {
          setSelectedNode({name: nodeName,...mainTree[nodeName]})
          setNodeProp({name: nodeName,...mainTree[nodeName]})
        } else if (nodeName.startsWith('Mounts/') && nodeName in mountedTree) {
          setSelectedNode({name: nodeName,...mountedTree[nodeName]})
          setNodeProp({name: nodeName,...mountedTree[nodeName]})
        } else if (nodeName.startsWith('Shared/')) {
          if (nodeName in sharedTree) setSelectedNode({...sharedTree[nodeName], name: nodeName})
          else setSelectedNode({id: '0', name: nodeName, created: ''})
          setNodeProp({id: '', name: '', created: ''})
        } else if (nodeName.startsWith('Bin/')) {
          if (nodeName in binTree) setSelectedNode({...binTree[nodeName], name: nodeName})
          else setSelectedNode({id: '0', name: nodeName, created: ''})
          setNodeProp({id: '', name: '', created: ''})
        } else if (nodeName.startsWith(`${attachedS3}/`)) {
          if (nodeName in s3Tree) setSelectedNode({...s3Tree[nodeName], name: nodeName})
          else setSelectedNode({id: '0', name: nodeName, created: ''})
          setNodeProp({id: '', name: '', created: ''})
        } else {
          setSelectedNode(null)
          setNodeProp({id: '', name: '', created: ''})
        }
      }
    }
    // console.log(selectedItems)
  }, [selectedItems]) // eslint-disable-line

  const MemoizedTree = useMemo(() =>
    <RenderTree
      mainTree={mainTree}
      mountedTree={mountedTree}
      sharedTree={sharedTree}
      binTree={binTree}
      s3Tree={s3Tree}
      width={width}
      height={height}
      selectNode={selectNode}
      focusNodeId={focusNodeId}
      searchTerm={searchKey}
    />
  , [mainTree, mountedTree, sharedTree, binTree, s3Tree, width, height, focusNodeId, searchKey])

  return (
    <Box className='d-flex flex-column h-100 w-100'>
      <Box className='d-flex justify-content-end p-2 gap-2'>
        <IconButton onClick={handleSearch} aria-label="search"><Search /></IconButton>

        {searchOpen
          ? <Box className='d-flex gap-2 align-items-center justify-content-center flex-grow-1'>
              <TextField
                defaultValue={searchKey}
                onChange={handleSearchKeyChange}
                name='name'
                label="Name"
                variant="outlined"
                autoComplete="off"
                size="small"
                fullWidth
              />
              <IconButton onClick={closeSearchModal} aria-label="close search"><Close /></IconButton>
          </Box>
          : <>
            <IconButton onClick={handleRename} aria-label="rename" 
              disabled={!(selectedNode && (selectedNode.name.startsWith('My Folder/') || selectedNode.name.startsWith('Mounts/')) && selectedNode.name.split('/').filter(Boolean).length > 1)}
            >
              <DriveFileRenameOutline />
            </IconButton>

            <IconButton onClick={handleUploadNode} aria-label="upload"
              disabled={!(selectedNode &&
                (
                  selectedNode.name.startsWith('My Folder/') ||
                  (
                    selectedNode.name.startsWith('Mounts/') &&
                    selectedNode.name.split('/').filter(Boolean).length > 1
                  )
                ) &&
                selectedNode.name.endsWith('/') &&
                selectedNode.s3_dir === undefined
              )}
            >
              <FileUpload />
            </IconButton>

            <IconButton onClick={handleNewNode} aria-label="new folder"
              disabled={!(selectedNode && (selectedNode.name.startsWith('My Folder/') || selectedNode.name.startsWith('Mounts/')) && selectedNode.name.endsWith('/'))}
            >
              <CreateNewFolder />
            </IconButton>

            <IconButton onClick={handleDelete} aria-label="delete"
              disabled={!(selectedNode && (selectedNode.name.startsWith('My Folder/') || selectedNode.name.startsWith('Mounts/')) && selectedNode.name.split('/').filter(Boolean).length > 1)}
            >
              <DeleteOutline />
            </IconButton>

            <Box>
              <IconButton onClick={handleSortMenu} disabled={!selectedNode} aria-haspopup="true"><Sort /></IconButton>
              <Menu anchorEl={sortMenuEl} open={sortMenuOpen} onClose={closeSortMenu}>
                <MenuItem onClick={() => handleSortTree('name-asc')}>
                  Name&nbsp;<KeyboardArrowDown/>
                </MenuItem>
                <MenuItem onClick={() => handleSortTree('name-desc')}>
                  Name&nbsp;<KeyboardArrowUp/>
                </MenuItem>
                <MenuItem onClick={() => handleSortTree('date-asc')}>
                  Date&nbsp;<KeyboardArrowDown/>
                </MenuItem>
                <MenuItem onClick={() => handleSortTree('date-desc')}>
                  Date&nbsp;<KeyboardArrowUp/>
                </MenuItem>
              </Menu>
            </Box>

            <Box>
              <IconButton onClick={handleNodeMenu} aria-haspopup="true"><MenuIcon /></IconButton>
              <Menu anchorEl={nodeMenuEl} open={nodeMenuOpen} onClose={closeNodeMenu}>
                {/* <MenuItem onClick={handleSelectNodeItems}>{showCheckbox ? 'Unselect all' : 'Select items'}</MenuItem> */}
                <Tooltip title='Compare 2 slides side-by-side' placement="right" enterDelay={1000}>
                  <MenuItem onClick={compareImageViewer}>Toggle Compare</MenuItem>
                </Tooltip>
                {selectedNode && !selectedNode.name.startsWith('Bin/') && selectedNode.name.split('/').filter(Boolean).length > 1
                  ? selectedNode.name.endsWith('/') // Folder
                    ? <>
                      {selectedNode.name.startsWith('My Folder/') && copiedNode && copiedNode.name !== selectedNode.name && <MenuItem onClick={confirmCopyPaste}>Paste</MenuItem>}
                      {selectedNode.name.startsWith('My Folder/') && <MenuItem onClick={copyNode}>Copy</MenuItem>}
                      {!selectedNode.name.startsWith(`${attachedS3}/`) && <MenuItem onClick={predictAllFiles}>Manage Predictions</MenuItem>}
                      {(selectedNode.name.startsWith('My Folder/') || selectedNode.name.startsWith('Mounts/')) && <MenuItem onClick={handleOpenShare}>Share</MenuItem>}
                      <MenuItem onClick={downloadAllXMLHandler}>Download XML ZIP</MenuItem>
                      <MenuItem onClick={downloadAllGeoJSONHandler}>Download JSON ZIP</MenuItem>
                      <MenuItem onClick={downloadAllCSVHandler}>Download CSV</MenuItem>
                      {selectedNode.name.startsWith('Mounts/') && !(selectedNode.sync_disabled) && selectedNode.s3_name && <MenuItem onClick={handleS3MountSync}>Sync With S3</MenuItem>}
                      {selectedNode.name.startsWith(`${attachedS3}/`) && <MenuItem onClick={handleS3BatchUpload}>Upload all slides</MenuItem>}
                    </>
                    : <>
                      {(selectedNode.name.startsWith(`${attachedS3}/`) || selectedNode.name.startsWith('My Folder/')) && <MenuItem onClick={copyNode}>Copy</MenuItem>}
                      <MenuItem onClick={predictionStatusHandler}>Manage Predictions</MenuItem>
                      {(isPrivate || isDev) && <MenuItem onClick={handlePatientModal}>Add to Patient</MenuItem>}
                      <MenuItem onClick={uploadXMLHandler}>Upload XML/JSON</MenuItem>
                      {(selectedNode.name.startsWith('My Folder/') || selectedNode.name.startsWith('Mounts/')) && <MenuItem onClick={handleOpenShare}>Share</MenuItem>}
                      <MenuItem onClick={downloadImageHandler}>Download Image</MenuItem>
                      <MenuItem onClick={downloadAnnoHandler}>Download Annotations</MenuItem>
                      <MenuItem onClick={showLogs}>View Log</MenuItem>
                      <MenuItem onClick={getFileInfoHandler}>File Info</MenuItem>
                      {/* {selectedNode.name.startsWith('Bin/') && <MenuItem onClick={restoreFile}>Restore File</MenuItem>} */}
                      <MenuItem onClick={reloadViewer}>Reload viewer</MenuItem>
                    </>
                  : null
                }
                {selectedNode && selectedNode.name.startsWith(`${attachedS3}/`) && selectedNode.name.endsWith('/') && <MenuItem onClick={handleS3Search}>Search in S3</MenuItem>}
              </Menu>
            </Box>
          </>
        }

        <IconButton onClick={() => handleShowTree()} aria-label='Minimize directory'>
          <ChevronLeft />
        </IconButton>
      </Box>

      <Box ref={ref} className='tree-parent'>{MemoizedTree}</Box>

      {/* Create/Rename node */}
      <Modal open={renameOpen} onClose={closeNodeNameModal}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">
              {nodeProp.created.length ? 'Rename' : 'New Folder'}
            </Typography>
            <Box>
              <TextField
                defaultValue={nodeProp.name.split('/').filter(Boolean).pop()}
                onChange={handleNameChange}
                name='name'
                label="Name"
                variant="outlined"
                autoComplete="off"
                fullWidth
              />
              {nodeNameError.length > 1 &&
                <Typography color='error' variant='caption'>{nodeNameError}</Typography>
              }
            </Box>
            <Box className='d-flex gap-3 justify-content-end'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={closeNodeNameModal}>Close</Button>
                  <Button
                    variant="contained"
                    onClick={nodeProp.created.length ? renameNode : createNode}
                    disabled={!nodeProp.name.length || !(nodeProp.name !== selectedNode?.name) || nodeProp.name.indexOf('/') !== -1}
                  >
                    Confirm
                  </Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Delete node */}
      <Modal open={deleteOpen} onClose={closeDeleteModal}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Are you sure?</Typography>
            <Typography>
              Delete '{selectedNode?.name.split('/').filter(Boolean).pop()}' and its contents
            </Typography>
            <Box className='d-flex gap-3 justify-content-end'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={closeDeleteModal}>Close</Button>
                  <Button variant="contained" color="error" onClick={deleteNode}>Delete</Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Search file name */}
      <Modal open={s3SearchOpen} onClose={closeS3SearchModal}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Search File</Typography>
            <Box>
              <TextField
                defaultValue={s3SearchKey}
                onChange={handleS3SearchKeyChange}
                name='name'
                label="Name"
                variant="outlined"
                autoComplete="off"
                fullWidth
              />
              {searchFinds.length
                ? <List className='p-3'>
                  <ListItem disablePadding>
                    <ListItemText primary={`Search results (${searchFinds.length < 1000 ? searchFinds.length : '1000+'})`} />
                  </ListItem>
                  {searchFinds.length < 1000
                    ? searchFinds.map((name, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemButton onClick={() => selectSearchFind(index)} dense>
                          <ListItemText
                            className='text-limit'
                            primary={name.split('/').filter(Boolean).pop()}
                            secondary={<span className='m-0 fs-7' title={name}>{name}</span>}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                    : <ListItem disablePadding>
                      <ListItemText primary='Your search returned too many matches. Try to narrow down the results.' />
                    </ListItem>
                  }
                </List>
                : null
              }
              <Typography id='search-key-error' className='d-none text-danger m-2'>Name not found</Typography>
            </Box>
            <Box className='d-flex gap-3 justify-content-end'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={closeS3SearchModal}>Close</Button>
                  <Button variant="contained" onClick={searchNodeName} disabled={!s3SearchKey.trim().length}>Search</Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Shared node */}
      <Modal open={shareOpen} onClose={closeShareModal}>
        <Box sx={modalStyle}>
          <ShareSlide 
            slideData={selectedNode!}
            closeShareModal={closeShareModal}
            updateMainTree={updateMainTree}
            updateMountedTree={updateMountedTree}
          />
        </Box>
      </Modal>
      
      {/* Patients list */}
      <Modal open={showPatientModal} onClose={handlePatientModal}>
        <Box sx={modalStyle}>
          <PatientList
            slideData={selectedNode!}
            closeShareModal={handlePatientModal}
          />
        </Box>
      </Modal>

      {/* S3 moount sync */}
      <Modal open={syncS3Open} onClose={closeS3SyncModal}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Sync with source?</Typography>
            <Typography>
              Synchronize the folder with the source and include any new files
            </Typography>
            <Box className='d-flex gap-3 justify-content-end'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={closeS3SyncModal}>Close</Button>
                  <Button variant="contained" color="error" onClick={syncS3Mount}>Confirm</Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Logs */}
      <Modal open={logsOpen} onClose={() => setLogsOpen(false)}>
        <Box sx={modalStyle}>
          <LogInfoTable slideData={selectedNode!} />
        </Box>
      </Modal>

      {/* Slide info */}
      <Modal open={slideInfoOpen} onClose={() => setSlideInfoOpen(false)}>
        <Box sx={modalStyle}>
          <SlideInfo slideData={selectedNode!} />
        </Box>
      </Modal>

      {/* Prediction status form */}
      <Modal open={openPredictionForm} onClose={closePredictionForm}>
        <Box sx={modalStyle}>
          <PredictionForm slideData={selectedNode!} closePredictionForm={closePredictionForm} />
        </Box>
      </Modal>

      {/* Download annotation data as XML */}
      <Modal open={annoListOpen} onClose={closeAnnoDwnldOpen}>
        <Box sx={modalStyle}>
          <Box className='d-flex align-items-center flex-column justify-content-center gap-3'>
            {isFileTransfer
              ? <Dotpulse width={100} height={40} />
              : <>
                <Typography variant="h5" component="h1">Download File</Typography>
                <Box className='d-flex gap-3'>
                  <Button variant="contained" onClick={() => downloadAnnotationFile('xml')}>
                    XML&nbsp;&nbsp;<LuCodeXml size={24} />
                  </Button>
                  <Button variant="contained" onClick={() => downloadAnnotationFile('geojson')}>
                    GeoJSON&nbsp;&nbsp;<VscJson size={24} />
                  </Button>
                </Box>
                <FormGroup>
                  <FormControlLabel control={<Checkbox onChange={handleEditedOnlyCheck} />} label="User edits only" />
                </FormGroup>
              </>
            }
          </Box>
        </Box>
      </Modal>

      {/* Upload xml/json for slide */}
      <Modal open={openUploadToSlide} onClose={closeUploadToSlide}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Upload XML/JSON File</Typography>
              {isFileTransfer
                ? <LinearProgress />
                : <>
                  <FormControl fullWidth>
                    <Button startIcon={<UploadFile />} variant="outlined" component="label">
                      Upload File
                      <input
                        onChange={handleUploadFile}
                        name='upload-file'
                        type="file"
                        accept='.xml,.json,.geojson,application/xml,application/json,application/geojson'
                        hidden
                      />
                    </Button>
                    <Typography className='text-limit' sx={{maxWidth: '20vw'}}>{uploadFile ? uploadFile.name : ''}</Typography>
                    <Typography id='unknown-file-error' className='text-danger d-none'>Only .xml, .json and .geojson files are supported</Typography>
                  </FormControl>
                  <Box className='d-flex gap-3 justify-content-center'>
                    <Button variant="outlined" onClick={closeUploadToSlide}>Close</Button>
                    <Button variant="contained" onClick={uploadFileToSlide} disabled={!uploadFile}>Upload</Button>
                  </Box>
                </>
              }
          </Box>
        </Box>
      </Modal>

      {/* Copy Paste Nodes */}
      <Modal open={openCopyPasteConfirm} onClose={cancelCopyPaste}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4' sx={{maxWidth: 500}}>
            <Typography variant="h5" component="h1">Paste Copied Node</Typography>
            <Typography className='text-wrap'>
              Add <Typography component='i' color='primary'>{copiedNode ? copiedNode.name : 'node'}</Typography> you copied earlier to&nbsp;
              <Typography component='i' color='primary'>{selectedNode ? selectedNode.name : 'this'}</Typography> position.
            </Typography>
            <Box className='d-flex gap-3 justify-content-center'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={cancelCopyPaste}>Cancel</Button>
                  <Button variant="contained" onClick={handleNodePaste}>Confirm</Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Predict all files inside a folder */}
      <Modal open={openPredictFolder} onClose={closePredictFolder}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4' sx={{maxWidth: 400}}>
            <Typography variant="h5" component="h1">Predictions</Typography>
            <Box className='d-flex flex-column gap-4'>
              <Typography className='text-wrap'>
                All slides in the '{selectedNode?.name.split('/').filter(Boolean).pop()?.replace(/<\d+>_/g, '')}' 
                folder is selected, already predicted slides would not be affected by this
              </Typography>
              <FormControl fullWidth>
                <InputLabel>Prediction Type</InputLabel>
                <Select name='prediction_type' label="Prediction Type" value={slideForm.prediction_type} onChange={handleSlideForm}>
                  <MenuItem value='gs'>Gleason grading</MenuItem>
                  <MenuItem value='bcr'>Outcome prediction</MenuItem>
                  <MenuItem value='bcr+gs'>Gleason grading + Outcome prediction</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Request Type</InputLabel>
                <Select name='request_type' label="Request Type" value={slideForm.request_type} onChange={handleSlideForm}>
                  <MenuItem value='start'>Start</MenuItem>
                  <MenuItem value='stop'>Stop</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box className='d-flex gap-3 justify-content-end'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={closePredictFolder}>Close</Button>
                  <Button variant="contained" onClick={predictFolder}>Submit</Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Download XML/CSV/GeoJSON from folder */}
      <Modal open={openDownlFolderData}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Download {downloadListType}</Typography>
            {isFileTransfer
              ? <>
                {downloadedBytes > 0
                  ? <Typography>Downloading... {(downloadedBytes / 1024 / 1024).toFixed(2)} MB</Typography>
                  : null
                }
                <LinearProgress />
              </>
              : <Box>
                <Typography>
                  Download prediction data for&nbsp;{selectedNode?.name.startsWith(`${attachedS3}/`) ? 'only the root' : 'all the'}&nbsp;slides in&nbsp;<br/>
                  <Typography variant="caption" bgcolor='grey' color='white' sx={{py: '2px', px: '6px', borderRadius: 100}}>
                    {selectedNode ? selectedNode.name.replace(/<\d+>_/g, '') : 'selected'}
                  </Typography>
                </Typography>
                {downloadListType.toLowerCase()!=='csv' &&
                  <FormGroup>
                    <FormControlLabel control={<Checkbox onChange={handleEditedOnlyCheck} />} label="User edits only" />
                  </FormGroup>
                }
              </Box>
            }
            <Box className='d-flex gap-3 justify-content-center'>
              <Button variant="outlined" onClick={closeDownlFolderData}>Cancel</Button>
              {!isFileTransfer && <Button variant="contained" onClick={downloadFolderData}>Confirm</Button>}
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Upload files form S3 folder to database */}
      <Modal open={uploadS3Slides} onClose={closeUploadS3Slides}>
        <Box sx={modalStyle}>
          <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Upload slides to database?</Typography>
            <Typography>
              Upload all slides in the root directory of <Typography component='span' color='primary'>{selectedNode?.name}</Typography> folder,
              this will start both the tiling and tissue area processes.
            </Typography>
            <Box className='d-flex gap-3 justify-content-end'>
              {apiWaiting
                ? <CircularProgress size={30} />
                : <>
                  <Button variant="outlined" onClick={closeUploadS3Slides}>Close</Button>
                  <Button variant="contained" onClick={continueUplaodS3Slides}>Confirm</Button>
                </>
              }
            </Box>
          </Box>
        </Box>
      </Modal>

      {selectedNode && uploadOpen &&
        <UploadSlide
          open={uploadOpen}
          closeModal={handleUploadClose}
          saveFile={saveFile}
          saveMounts={saveMounts}
          selectedNode={selectedNode}
        />
      }
    </Box>
  )
}

export default Directories